import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSetSiteHostnames, useUpdateClient, usePlans } from "@/hooks/useClients";
import { ClientFormFields } from "@/components/ClientFormFields";
import {
  clientToFormValues,
  toClientProfileInput,
  validateClientForm,
  type ClientFormErrors,
  type ClientFormValues,
} from "@/components/client-form";
import { ApiError, type SuperAdminClient } from "@/lib/api";

/** One host per line; blank lines dropped. The server normalizes and validates the rest. */
function parseHostnames(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The client being edited. Creation lives in the onboarding wizard's step 1. */
  client: SuperAdminClient;
};

/**
 * Edit a client from the Clients list.
 *
 * The fields live in `ClientFormFields`, shared with step 1 of the onboarding wizard —
 * which is also where a client is CREATED, so the same data is never captured in two
 * places. This component owns only the dialog, the mutation and the toast.
 */
export function ClientFormModal({ open, onOpenChange, client }: Props): React.JSX.Element {
  const { data: plans } = usePlans();
  const updateClient = useUpdateClient();
  const setSiteHostnames = useSetSiteHostnames();
  const isPending = updateClient.isPending || setSiteHostnames.isPending;

  const [values, setValues] = useState<ClientFormValues>(() => clientToFormValues(client));
  const [errors, setErrors] = useState<ClientFormErrors>({});
  // Not part of ClientFormFields: that one is shared with the onboarding wizard,
  // and a domain is connected once a site exists, not while a client is created.
  const savedHostnames = (client.siteHostnames ?? []).join("\n");
  const [hostnamesText, setHostnamesText] = useState(savedHostnames);
  const [hostnamesError, setHostnamesError] = useState<string | null>(null);

  // Reset the form whenever the modal opens (for the current client).
  useEffect(() => {
    if (!open) return;
    setValues(clientToFormValues(client));
    setErrors({});
    setHostnamesText((client.siteHostnames ?? []).join("\n"));
    setHostnamesError(null);
  }, [open, client]);

  function handleChange(next: ClientFormValues): void {
    setValues(next);
    // Clear messages as the operator types; they come back on the next submit.
    if (Object.keys(errors).length > 0) setErrors({});
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const found = validateClientForm(values, { isEdit: true });
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    try {
      await updateClient.mutateAsync({ id: client.id, ...toClientProfileInput(values) });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar el cliente");
      return;
    }

    const hostnames = parseHostnames(hostnamesText);
    if (hostnames.join("\n") !== savedHostnames) {
      try {
        await setSiteHostnames.mutateAsync({ id: client.id, hostnames });
      } catch (err) {
        // A host another site holds (409) or one that is not a hostname (400):
        // the server's message names it, and it belongs next to the field.
        const message = err instanceof Error ? err.message : "No se pudo guardar el dominio";
        setHostnamesError(message);
        toast.error(err instanceof ApiError && err.status === 409 ? message : `Cliente guardado; el dominio no: ${message}`);
        return;
      }
    }

    toast.success("Cliente actualizado");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
        </DialogHeader>

        <form
          id="client-form"
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-4 overflow-y-auto"
        >
          <ClientFormFields
            values={values}
            onChange={handleChange}
            errors={errors}
            plans={plans ?? []}
            disabled={isPending}
            isEdit
          />

          <div className="mt-2 flex flex-col gap-1.5 border-t border-ink-200 pt-4">
            <label htmlFor="site-hostnames" className="text-sm font-semibold text-ink-900">
              Sitio web <span className="font-normal text-ink-400">(dominios)</span>
            </label>
            <textarea
              id="site-hostnames"
              value={hostnamesText}
              onChange={(e) => {
                setHostnamesText(e.target.value);
                setHostnamesError(null);
              }}
              disabled={isPending}
              rows={2}
              placeholder={"rrautoindustrial.com\nrr.loxago.ai"}
              className="rounded-md border border-ink-200 bg-white px-2 py-1.5 font-mono text-sm text-ink-900 disabled:opacity-50"
            />
            <span className="text-xs text-ink-500">
              Uno por línea, sin https:// ni www. (el www. se redirige solo). Agrégalo también en Vercel
              (proyecto loxago-sites); conectarlo aquí ya lo permite como origen del widget.
            </span>
            {hostnamesError && <span className="text-xs text-red-500">{hostnamesError}</span>}
          </div>
        </form>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button type="submit" form="client-form" disabled={isPending}>
            {isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
